import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["inputLabel", "name", "isDisabled", "inputPlaceholder", "defaultStartDate", "isError", "dataTestId", "isRightIcon", "onSelectDate", "locale", "popperPlacement", "skipFormatRules", "minDate", "maxDate", "dateFormat", "datepickerStyles", "displayDateFormat", "customHeader", "isClearable", "openToDate"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { CloseIcon } from '@chakra-ui/icons';
import { Box, FormControl, FormLabel, Input, InputGroup, InputLeftElement, InputRightElement, useStyleConfig } from '@chakra-ui/react';
import { formatDate, isSameDate } from '@whitbread-eos/utils';
import classNames from 'classnames';
import { add, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import Calendar from '../../assets/icons/Calendar';
import DatePickerGlobalStyles from '../../theme/components/DatePickerGlobalStyles';
import { formatDataTestId } from '../../utils/formatters';
import Icon from '../Icon';
import CustomHeader from './CustomHeader';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export function getFormattedDate(startDate, dateFormat, labels, skipFormatRules, locale) {
  if (skipFormatRules) {
    return startDate ? format(startDate, dateFormat) : '';
  }
  var today = new Date();
  var tomorrow = add(today, {
    days: 1
  });
  if (isSameDate(startDate, today)) {
    return "".concat(labels.todayLabel);
  }
  if (isSameDate(startDate, tomorrow)) {
    return "".concat(labels.tomorrowLabel);
  }
  return startDate ? formatDate(startDate.toDateString(), dateFormat, locale) : '';
}
export default function SingleDatePicker(props) {
  var inputLabel = props.inputLabel,
    name = props.name,
    isDisabled = props.isDisabled,
    inputPlaceholder = props.inputPlaceholder,
    defaultStartDate = props.defaultStartDate,
    isError = props.isError,
    dataTestId = props.dataTestId,
    isRightIcon = props.isRightIcon,
    onSelectDate = props.onSelectDate,
    locale = props.locale,
    popperPlacement = props.popperPlacement,
    skipFormatRules = props.skipFormatRules,
    minDate = props.minDate,
    maxDate = props.maxDate,
    dateFormat = props.dateFormat,
    datepickerStyles = props.datepickerStyles,
    showDateFormat = props.displayDateFormat,
    customHeader = props.customHeader,
    isClearable = props.isClearable,
    openToDate = props.openToDate,
    rest = _objectWithoutProperties(props, _excluded);
  var baseDatepickerStyles = useStyleConfig('Datepicker');
  registerLocale('de', de);
  var _useState = useState(defaultStartDate instanceof Date ? defaultStartDate : null),
    _useState2 = _slicedToArray(_useState, 2),
    startDate = _useState2[0],
    setStartDate = _useState2[1];
  var ref = useRef(null);
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isCalendarOpen = _useState4[0],
    setIsCalendarOpen = _useState4[1];
  var _getDatepickerStyles = getDatepickerStyles(datepickerStyles),
    inputBasicStyles = _getDatepickerStyles.inputBasicStyles,
    rightInputIconStyles = _getDatepickerStyles.rightInputIconStyles,
    leftInputIconStyles = _getDatepickerStyles.leftInputIconStyles,
    clearIconStyles = _getDatepickerStyles.clearIconStyles;
  var inputDisabledStyles = isDisabled ? disabledStyles : undefined;
  var inputStyles = _objectSpread(_objectSpread({}, inputBasicStyles(isError, isRightIcon)), inputDisabledStyles);
  var displayLabel = inputLabel;
  useEffect(() => {
    var checkIfClickedOutside = event => {
      if (isCalendarOpen && ref.current && !ref.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [isCalendarOpen]);
  useEffect(() => {
    if (!defaultStartDate) {
      setStartDate(null);
    }
  }, [defaultStartDate]);
  return _jsxs(Box, _objectSpread(_objectSpread({
    __css: baseDatepickerStyles,
    ref: ref
  }, datepickerStyles.datepickerInputElementStyles), {}, {
    children: [_jsx(DatePickerGlobalStyles, {}), _jsx(DatePicker, {
      locale: locale,
      popperPlacement: popperPlacement,
      selected: startDate,
      startDate: startDate,
      onChange: d => {
        handleDatepickerChange(d);
      },
      renderCustomHeader: customHeader ? renderCustomHeader : undefined,
      customInput: renderSingleDateInput(),
      minDate: minDate,
      maxDate: maxDate,
      useWeekdaysShort: true,
      calendarStartDay: 1,
      disabled: props.isDisabled,
      open: isCalendarOpen,
      renderDayContents: renderDayContents,
      dayClassName: getDayClassName,
      openToDate: startDate !== null && startDate !== void 0 ? startDate : openToDate || new Date()
    })]
  }));
  function getDayClassName(date) {
    var startWeek = startOfWeek(date, {
      weekStartsOn: 1
    });
    var endWeek = endOfWeek(date, {
      weekStartsOn: 1
    });
    var startMonth = startOfMonth(date);
    var endMonth = endOfMonth(date);
    var arr = [{
      'start-week': startWeek.getDate() === date.getDate()
    }, {
      'end-week': endWeek.getDate() === date.getDate()
    }, {
      'start-month': startMonth.getDate() === date.getDate()
    }, {
      'end-month': endMonth.getDate() === date.getDate()
    }, {
      'react-datepicker__day--disabled': isDayDisabled(date)
    }];
    return classNames(arr);
  }
  function isDayDisabled(date) {
    if (!startDate) return false;
    var resetMinDate = minDate.setHours(12, 0, 0, 0);
    return date.setHours(12, 0, 0, 0) < resetMinDate;
  }
  function renderSingleDateInput() {
    var displayDateFormat = showDateFormat !== null && showDateFormat !== void 0 ? showDateFormat : dateFormat;
    var value = getFormattedDate(startDate, displayDateFormat, props.labels, skipFormatRules, locale);
    return _jsx(FormControl, _objectSpread(_objectSpread({
      ref: ref
    }, rest), {}, {
      children: _jsxs(InputGroup, {
        children: [isRightIcon ? _jsx(InputRightElement, _objectSpread(_objectSpread({
          pointerEvents: "none"
        }, rightInputIconStyles(isDisabled)), {}, {
          children: _jsx(Icon, {
            svg: _jsx(Calendar, {})
          })
        })) : _jsx(InputLeftElement, _objectSpread(_objectSpread({
          pointerEvents: "none"
        }, leftInputIconStyles(isDisabled)), {}, {
          children: _jsx(Icon, {
            svg: _jsx(Calendar, {})
          })
        })), isClearable && value && _jsx(InputRightElement, _objectSpread(_objectSpread({}, clearIconStyles(isRightIcon)), {}, {
          onClick: () => {
            setStartDate(null);
            onSelectDate === null || onSelectDate === void 0 || onSelectDate(null);
          },
          children: _jsx(Icon, {
            svg: _jsx(CloseIcon, {
              w: "3",
              color: "lightGrey1"
            })
          })
        })), displayLabel && _jsx(FormLabel, _objectSpread(_objectSpread({
          "data-testid": formatDataTestId(dataTestId, "".concat(name, "-label")),
          pos: "absolute"
        }, labelStyle(isError, value)), {}, {
          htmlFor: name,
          children: inputLabel
        })), _jsx(Input, _objectSpread(_objectSpread({
          placeholder: inputPlaceholder || inputLabel,
          "aria-label": "datepicker-input",
          value: value,
          onChange: event => {
            var _props$onInputChange;
            props === null || props === void 0 || (_props$onInputChange = props.onInputChange) === null || _props$onInputChange === void 0 || _props$onInputChange.call(props, event.target.value);
          },
          isDisabled: isDisabled,
          onClick: () => {
            setIsCalendarOpen(true);
          }
        }, inputStyles), {}, {
          "data-testid": formatDataTestId(dataTestId, 'SingleDatePicker')
        }))]
      })
    }));
  }
  function renderDayContents(day) {
    return _jsxs(_Fragment, {
      children: [_jsx("div", {
        className: "highlight"
      }), _jsx("div", {
        className: "day-number",
        children: day
      })]
    });
  }
  function handleDatepickerChange(d) {
    setIsCalendarOpen(false);
    setStartDate(d);
    onSelectDate === null || onSelectDate === void 0 || onSelectDate(d);
  }
}
function getDatepickerStyles(datepickerStyles) {
  var inputBasicStyles = (error, isRightIcon) => {
    return _objectSpread(_objectSpread({
      padding: 'var(--chakra-space-lg) var(--chakra-space-lg) var(--chakra-space-lg) 0',
      paddingLeft: isRightIcon ? 'var(--chakra-space-md)' : 'var(--chakra-space-2xl)',
      outline: 'none',
      cursor: 'pointer',
      border: '0.063rem solid var(--chakra-colors-lightGrey1)',
      borderRadius: 'var(--chakra-space-xs)',
      borderTopRightRadius: 'var(--chakra-space-xs)',
      borderBottomRightRadius: 'var(--chakra-space-xs)',
      textOverflow: 'ellipsis',
      _active: {
        border: '0.063rem solid var(--chakra-colors-primary)',
        borderRadius: 'var(--chakra-space-xs)',
        borderColor: 'var(--chakra-colors-primary)'
      },
      _target: {
        border: '0.063rem solid var(--chakra-colors-primary)',
        borderRadius: 'var(--chakra-space-xs)',
        borderColor: 'var(--chakra-colors-primary)'
      },
      _disabled: {
        border: 'none'
      },
      _hover: {
        borderColor: 'none'
      },
      _focus: {
        zIndex: '0',
        borderWidth: '2px',
        borderColor: error ? 'error' : 'primary'
      },
      _placeholder: {
        color: 'darkGrey2'
      }
    }, datepickerStyles.datepickerInputElementStyles), datepickerStyles === null || datepickerStyles === void 0 ? void 0 : datepickerStyles.errorInputElementStyles);
  };
  var rightInputIconStyles = isDisabled => {
    return _objectSpread(_objectSpread({
      top: '0.313rem',
      right: 'var(--chakra-space-sm)'
    }, datepickerStyles.iconStyles), {}, {
      opacity: isDisabled ? '0.4' : 'unset'
    });
  };
  var leftInputIconStyles = isDisabled => {
    return _objectSpread(_objectSpread({
      top: 'var(--chakra-space-md)'
    }, datepickerStyles.iconStyles), {}, {
      opacity: isDisabled ? '0.4' : 'unset'
    });
  };
  var clearIconStyles = isRightIcon => _objectSpread(_objectSpread({}, datepickerStyles.iconStyles), {}, {
    right: isRightIcon ? '2xl' : 'sm'
  });
  return {
    inputBasicStyles,
    rightInputIconStyles,
    leftInputIconStyles,
    clearIconStyles
  };
}
function renderCustomHeader(_ref) {
  var date = _ref.date,
    changeYear = _ref.changeYear,
    changeMonth = _ref.changeMonth,
    decreaseMonth = _ref.decreaseMonth,
    increaseMonth = _ref.increaseMonth,
    prevMonthButtonDisabled = _ref.prevMonthButtonDisabled,
    nextMonthButtonDisabled = _ref.nextMonthButtonDisabled;
  return _jsx(CustomHeader, {
    date: date,
    changeYear: changeYear,
    changeMonth: changeMonth,
    decreaseMonth: decreaseMonth,
    increaseMonth: increaseMonth,
    prevMonthButtonDisabled: prevMonthButtonDisabled,
    nextMonthButtonDisabled: nextMonthButtonDisabled
  });
}
var labelStyle = (error, value) => ({
  h: '1.25rem',
  w: 'fit-content',
  fontSize: 'sm',
  fontWeight: 'normal',
  align: 'center',
  px: 'xs',
  ml: '0.750rem',
  top: '-0.625rem',
  backgroundColor: 'baseWhite',
  zIndex: '1',
  color: error ? 'error' : 'darkGrey1',
  display: value ? 'block' : 'none',
  _focus: {
    color: error ? 'error' : 'primary',
    display: 'block'
  }
});
var disabledStyles = {
  _disabled: {
    opacity: '0.4',
    cursor: 'not-allowed'
  },
  _active: {
    opacity: '0.4'
  }
};